import styled from '@emotion/styled';
import { breakpoints } from '~/theme/breakpoints';
import { ifProp } from 'styled-tools';
import RouteIcon from '~/icons/route.svg';
import { Link } from '~/shared/components';

interface StyledContainerProps {
    spacingTop?: string;
    spacingBottom?: string;
    border?: string;
}

//Secondary Tab Content
export const StyledTabSecondaryContent = styled.div(({ theme }) => ({
    border: `1px solid ${theme.colors.black}`,
    borderRadius: theme.space[1],
    marginTop: theme.space[3],
    width: '100%',
    [breakpoints.md]: {
        paddingTop: theme.space[6],
    },
}));

export const StyledStoreImage = styled.div(({ theme }) => ({
    paddingTop: theme.space[3],
    margin: '0 -26px',
}));

export const StyledStoreDescription = styled.div(({ theme }) => ({
    paddingTop: theme.space[3],
    fontSize: theme.fontSizes.xs,
}));

export const StyledListSelector = styled.li(({ theme }) => ({
    width: '100%',
    listStyle: 'none',
    padding: theme.space[3],
    borderBottom: `1px solid ${theme.colors.brownLight}`,

    [breakpoints.md]: {
        padding: `${theme.space[3]} ${theme.space[3]} ${theme.space[3]} ${theme.space[5]}`,
    },

    '& .storeSummary': {
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        ':focus-visible': {
            boxShadow: theme.shadows.accessibility,
        },
    },

    '& span.selectorOption': {
        display: 'none',
    },
    '& span>span': {
        ...theme.mixins.useTextStyle('bodySmall'),
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        color: `${theme.colors.black}`,
        marginBottom: theme.space[1],
    },

    '& strong': {
        marginBottom: theme.space[1],
        textTransform: 'uppercase',
        fontSize: theme.fontSizes.sm,
        color: `${theme.colors.black}`,
    },
    '&:last-child': {
        borderBottom: `none`,
    },
    '& input[type=radio]': {
        display: 'none',
    },
}));

export const StyledNoStoreFound = styled.li(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    listStyle: 'none',
    padding: `${theme.space[5]} ${theme.space[3]}`,
    gap: theme.space[2],
    [breakpoints.md]: {
        padding: `${theme.space[5]} ${theme.space[5]}`,
    },
}));

export const StyledUlListSelector = styled.ul(({ theme }) => ({
    position: 'relative',

    width: '100%',
    listStyle: 'none',
    padding: 0,
    '.pinSelected': {
        backgroundColor: theme.colors.grey10,
    },
    '.extendCard': {},
    [breakpoints.md]: {
        border: 0,
        borderRadius: theme.space[1],
        height: '520px',
        overflowY: 'auto',
    },
    [breakpoints.lg]: {
        height: '55vh',
    },
}));

export const StyledSpan = styled.em(({ theme }) => ({
    color: `${theme.colors.black}`,
    marginLeft: theme.space['1'],
}));

export const StyledEm = styled.em<{ storeOpened: boolean }>(
    ({ theme }) => ({
        color: `${theme.colors.redMedium}`,
        marginRight: theme.space['1'],
    }),
    ifProp('storeOpened', ({ theme }) => ({
        color: `${theme.colors.greenMedium}`,
    })),
);

export const StyledLinkRoute = styled(Link)(({ theme }) => ({
    color: `${theme.colors.black}`,
    fontSize: theme.fontSizes['2xs'],
    textAlign: 'center',
}));

export const StyledCardStore = styled.div(({ theme }) => ({
    color: `${theme.colors.black}`,
    fontSize: theme.fontSizes['2xs'],
}));

export const StyleRoute = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
}));
export const StyleRouteIcon = styled(RouteIcon)(({ theme }) => ({
    width: '100%',
    height: '48px',
    marginBottom: theme.space[3],
}));
export const StyledInfo = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'left',
    minWidth: '70%',
}));

export const StyledInfoRTE = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'left',
    minWidth: '70%',
    width: '100%',
    a: {
        color: `${theme.colors.black}`,
    },
    p: {
        cursor: 'pointer',
        lineHeight: theme.space[7],
        ...theme.mixins.useTextStyle('bodySmall'),
    },
}));

export const StyledStoreWrapper = styled.div<StyledContainerProps>(
    ({ theme, spacingTop, spacingBottom, border }) => {
        const getPadding = (value?: string) => {
            switch (value) {
                case 'small':
                    return theme.space[3];
                case 'default':
                    return theme.space[4];
                default:
                    return 0;
            }
        };

        const borderStyles =
            border === 'top'
                ? { borderTop: `1px solid ${theme.colors.brownLight}` }
                : border === 'bottom'
                  ? { borderBottom: `1px solid ${theme.colors.brownLight}` }
                  : { border: 'none' };

        return {
            paddingTop: spacingTop ? getPadding(spacingTop) : theme.space[3],
            paddingBottom: getPadding(spacingBottom),
            ...borderStyles,
        };
    },
);

export const StyledStoreImageContainer = styled.div(() => ({
    margin: '0 -26px',
}));

export const StyledStoreOpeningHoursContainer = styled.div(({ theme }) => ({
    width: '100%',
    listStyle: 'none',
    textTransform: 'uppercase',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    lineHeight: theme.space[7],
    ...theme.mixins.useTextStyle('bodySmall'),
}));

export const StyledTextSpotContainer = styled.div(({ theme }) => ({
    p: {
        ...theme.mixins.useTextStyle('bodySmall'),
    },
    a: {
        color: `${theme.colors.black}`,
    },
}));

export const StyledInfoSpan = styled.span(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'start',
    ...theme.mixins.useTextStyle('bodySmall'),
}));

export const StyledLinkViewMore = styled(Link)(({ theme }) => ({
    color: `${theme.colors.black}`,
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.medium,
    textDecoration: 'underline',
    margin: `${theme.space[2]} 0`,
    display: 'block',
}));

export const StyledMainSpan = styled.span(({ theme }) => ({
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    lineHeight: theme.space[7],
    ...theme.mixins.useTextStyle('bodySmall'),
}));
